import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Build as BuildIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

const Mantenimiento = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const mantenimientosData = [
    { id: 1, equipo: 'BOP-001', tipo: 'Preventivo', descripcion: 'Inspección trimestral BOP', programado: '15/02/2026', estado: 'Programado', tecnico: 'Carlos Mendoza' },
    { id: 2, equipo: 'GR-012', tipo: 'Preventivo', descripcion: 'Cambio de aceite y filtros', programado: '08/02/2026', estado: 'Vencido', tecnico: 'Juan Rodríguez' },
    { id: 3, equipo: 'BOM-003', tipo: 'Correctivo', descripcion: 'Reparación de sello mecánico', programado: '03/02/2026', estado: 'En Proceso', tecnico: 'Pedro García' },
    { id: 4, equipo: 'GEN-008', tipo: 'Preventivo', descripcion: 'Service general generador', programado: '25/02/2026', estado: 'Programado', tecnico: 'Por Asignar' },
  ];

  const resumenStats = [
    { label: 'Programados', value: '12', icon: <ScheduleIcon />, color: theme.palette.info.main },
    { label: 'En Proceso', value: '3', icon: <BuildIcon />, color: theme.palette.warning.main },
    { label: 'Completados (Feb)', value: '8', icon: <CheckIcon />, color: theme.palette.success.main },
    { label: 'Vencidos', value: '2', icon: <WarningIcon />, color: theme.palette.error.main },
  ];

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'Programado': return 'info';
      case 'En Proceso': return 'warning';
      case 'Completado': return 'success';
      case 'Vencido': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Mantenimiento
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión de mantenimiento preventivo y correctivo
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Programar Mantenimiento
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {resumenStats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card sx={{ bgcolor: stat.label === 'Vencidos' ? alpha(theme.palette.error.main, 0.05) : 'transparent' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(stat.color, 0.1), color: stat.color }}>
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Alertas de Vencidos */}
      {selectedTab === 0 && (
        <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.error.main, 0.05), border: `1px solid ${theme.palette.error.main}` }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon color="error" />
              <Typography variant="body1" fontWeight={500} color="error.main">
                2 mantenimientos vencidos requieren atención inmediata
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tabla */}
      <Card>
        <CardContent>
          <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ mb: 3 }}>
            <Tab label="Todos" />
            <Tab label="Preventivos" />
            <Tab label="Correctivos" />
            <Tab label="Vencidos" />
          </Tabs>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell>Equipo</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Fecha Prog.</TableCell>
                  <TableCell>Técnico</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mantenimientosData.map((row) => (
                  <TableRow key={row.id} hover sx={{ bgcolor: row.estado === 'Vencido' ? alpha(theme.palette.error.main, 0.05) : 'transparent' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{row.equipo}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.tipo}
                        size="small"
                        variant="outlined"
                        color={row.tipo === 'Preventivo' ? 'info' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{row.descripcion}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {row.estado === 'Vencido' && <WarningIcon fontSize="small" color="error" />}
                        {row.programado}
                      </Box>
                    </TableCell>
                    <TableCell>{row.tecnico}</TableCell>
                    <TableCell>
                      <Chip label={row.estado} size="small" color={getEstadoColor(row.estado)} />
                    </TableCell>
                    <TableCell align="center">
                      {row.estado === 'Programado' && (
                        <Button size="small" variant="outlined">
                          Iniciar
                        </Button>
                      )}
                      {row.estado === 'En Proceso' && (
                        <Button size="small" variant="contained" color="success">
                          Completar
                        </Button>
                      )}
                      {row.estado === 'Vencido' && (
                        <Button size="small" variant="contained" color="error">
                          Reprogramar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Programar Mantenimiento</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Equipo</InputLabel>
                <Select label="Equipo">
                  <MenuItem value="1">BOP-001 - Blowout Preventer</MenuItem>
                  <MenuItem value="2">GR-012 - Grúa Telescópica</MenuItem>
                  <MenuItem value="3">GEN-008 - Generador 500KVA</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select label="Tipo">
                  <MenuItem value="preventivo">Preventivo</MenuItem>
                  <MenuItem value="correctivo">Correctivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fecha Programada" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción del Trabajo" multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Técnico Asignado</InputLabel>
                <Select label="Técnico Asignado">
                  <MenuItem value="1">Carlos Mendoza</MenuItem>
                  <MenuItem value="2">Juan Rodríguez</MenuItem>
                  <MenuItem value="3">Pedro García</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Horas Estimadas" type="number" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>Programar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Mantenimiento;
